import type { Document, SavedPath } from "../document/Document";
import type { PathModel } from "../document/PathModel";
import type { Command } from "./Command";

// SetWorkPathCommand — adds or replaces the Work Path in the document's
// workPaths array.  Photoshop behaviour: committing the Pen tool in "path"
// mode saves the path as "Work Path" (replacing any existing unnamed path).
//
// Invert: restores the previous workPaths array exactly.

export class SetWorkPathCommand implements Command {
  readonly kind = "set-work-path";

  constructor(
    private readonly path: PathModel,
    private readonly name: string = "Work Path",
    private readonly id: string = crypto.randomUUID(),
  ) {}

  apply(doc: Document): Document {
    const entry: SavedPath = { id: this.id, name: this.name, path: this.path };
    // Replace any existing "Work Path" entry (same behaviour as Photoshop)
    const existing = doc.workPaths ?? [];
    const withoutWork = existing.filter((p) => p.name !== "Work Path");
    return { ...doc, workPaths: [...withoutWork, entry] };
  }

  invert(before: Document, _after: Document): Command {
    return new RestoreWorkPathsCommand(before.workPaths ?? []);
  }
}

/** Restores the exact workPaths array (used as the invert of SetWorkPathCommand). */
class RestoreWorkPathsCommand implements Command {
  readonly kind = "restore-work-paths";

  constructor(private readonly paths: readonly SavedPath[]) {}

  apply(doc: Document): Document {
    return { ...doc, workPaths: this.paths };
  }

  invert(before: Document, _after: Document): Command {
    return new RestoreWorkPathsCommand(before.workPaths ?? []);
  }
}

// i31 — the Paths panel's delete/rename buttons need undoable commands too
// (per the "mutations go through Commands" convention every other panel
// follows). Both invert by restoring the exact prior workPaths array, same
// approach as SetWorkPathCommand's invert above.

/** Removes a saved path (by id) from the document's workPaths array. */
export class RemoveWorkPathCommand implements Command {
  readonly kind = "remove-work-path";

  constructor(private readonly id: string) {}

  apply(doc: Document): Document {
    const existing = doc.workPaths ?? [];
    return { ...doc, workPaths: existing.filter((p) => p.id !== this.id) };
  }

  invert(before: Document, _after: Document): Command {
    return new RestoreWorkPathsCommand(before.workPaths ?? []);
  }
}

/** Renames a saved path (by id) in the document's workPaths array. */
export class RenameWorkPathCommand implements Command {
  readonly kind = "rename-work-path";

  constructor(
    private readonly id: string,
    private readonly newName: string,
  ) {}

  apply(doc: Document): Document {
    const existing = doc.workPaths ?? [];
    return {
      ...doc,
      workPaths: existing.map((p) => (p.id === this.id ? { ...p, name: this.newName } : p)),
    };
  }

  invert(before: Document, _after: Document): Command {
    return new RestoreWorkPathsCommand(before.workPaths ?? []);
  }
}
